/**
 * SplatDepthPass — renders the gaussian splats' linear depth for translucent entities.
 *
 * Translucent hotspots, portals and custom meshes draw after the splats (TranslucentEntityLayer)
 * and fade out wherever a splat surface is in front of them (see utils/materialOpacity.ts). That
 * needs the splats' depth, which the forward pass never writes (splats blend without depth).
 *
 * This pass renders the World layer's splat draw with the engine's PREPASS shader variant, in
 * which each splat writes its centre's view depth wherever its alpha is above `alphaClip` (0.3),
 * into a single-channel linear-depth texture. It reuses the main camera's own splat draw, sort
 * and work buffer; a second camera would get its own sort and work buffer and double the cost.
 *
 * Cost control, so scenes without translucent entities pay nothing:
 * - the pass is only added to the frame graph while TranslucentEntityLayer holds a visible
 *   translucent mesh instance (an enabled entity below full opacity and above ~0), see
 *   `installSplatDepthPass`;
 * - when it is added but no visible translucent instance survived culling this frame, it clears
 *   the small target and skips the splat draw;
 * - the target renders at a reduced resolution (`scale`);
 * - once nothing has needed it for `releaseAfterMs`, the pass and its textures are freed, and
 *   they are allocated again the next time a translucent entity appears.
 *
 * Water surfaces read the same kind of map under their own names (uWaterDepthMap /
 * uWaterDepthParams). Normally that is this pass's texture. While the World splat draw is split
 * at the water (effects/water/waterSplatSplit.ts: splats in front of the water draw after it), the
 * water must see past them, so a second pass renders its map from only the splats behind the
 * water: the unified draws through the split's fragment hook in its depth mode (the forward
 * shader pass, which is the one that runs the hook), others with the prepass shader as here.
 */
import * as pc from 'playcanvas';
import { SPLAT_DEPTH_MAP_UNIFORM, SPLAT_DEPTH_PARAMS_UNIFORM, hasVisibleEntityTranslucency } from '../utils/materialOpacity';
import { collectUnifiedSplatDraws, isSplatMeshInstance } from './splatMeshInstances';

/** Soft fade range as a fraction of the fragment's depth, and its minimum in world units. */
export const SPLAT_DEPTH_SOFT_FRACTION = 0.02;
export const SPLAT_DEPTH_SOFT_MIN = 0.02;

/** The water's splat depth map and its params (x: 1 while valid), see waterShaders.ts. */
export const WATER_DEPTH_MAP_UNIFORM = 'uWaterDepthMap';
export const WATER_DEPTH_PARAMS_UNIFORM = 'uWaterDepthParams';

/** The split of the World splat draw at the water (effects/water/waterSplatSplit.ts), as this pass uses it. */
export interface SplatDepthWaterSplit {
    /** The split is on this frame (decided before the frame graph is built). */
    active(): boolean;
    /** Bind the hook's depth mode for drawing into `target`; false when it can't (the full map is drawn then). */
    bindDepthMode(target: pc.RenderTarget, alphaClip: number): boolean;
    /**
     * CPU-sort renderer: a `uMirrorClipPlane` value that culls the splats in front of the water in
     * the vertex stage, so the map is drawn with the (cheaper) prepass shader; null for the hook.
     */
    vertexCullPlane?(): Float32Array | null;
    /** Turn the hook off again. */
    bindOff(): void;
}

/** Which map a pass renders: every splat (translucent entities, particles, and normally the water) or the water's split map. */
export type SplatDepthPassKind = 'full' | 'water-split';

interface BlendDepthState {
    blendState: { blend: boolean };
    depthState: { write: boolean; test: boolean };
}

/** Engine internals the pass reads; typed locally because they are not in playcanvas.d.ts. */
interface ForwardRendererInternals {
    renderForwardLayer(
        camera: pc.Camera,
        renderTarget: pc.RenderTarget,
        layer: pc.Layer | null,
        transparent: boolean | undefined,
        shaderPass: number,
        options: { meshInstances: pc.MeshInstance[]; drawCallback?: () => void }
    ): void;
    buildFrameGraph(frameGraph: FrameGraphInternals, layerComposition: pc.LayerComposition): void;
}
interface FrameGraphInternals {
    renderPasses: pc.RenderPass[];
}
interface CulledInstances {
    opaque: pc.MeshInstance[];
    transparent: pc.MeshInstance[];
}
type LayerWithCulling = pc.Layer & { getCulledInstances(camera: pc.Camera): CulledInstances };

export class SplatDepthPass extends pc.RenderPass {
    readonly depthTexture: pc.Texture;
    private readonly floatTarget: boolean;
    private readonly splatInstances: pc.MeshInstance[] = [];
    private readonly unifiedSplatDraws = new Set<pc.MeshInstance>();
    private readonly params = new Float32Array(4);
    /** True when the last execute rendered the splats (the texture is valid for this frame). */
    rendered = false;
    /**
     * Other readers of the depth map besides translucent entities (water surfaces): when this
     * returns true the splats are drawn even with no translucent entity on screen.
     */
    alsoNeeded: (() => boolean) | null = null;
    /** Water planes on screen need the map (they read it under their own names while `servesWater`). */
    waterNeeded: (() => boolean) | null = null;
    /** This frame the full map is also the water's (no split): bind it under the water's names too. */
    servesWater = false;
    /** The water split ('water-split' passes): its depth mode draws only the splats behind the water. */
    waterSplit: SplatDepthWaterSplit | null = null;
    private readonly unifiedInstances: pc.MeshInstance[] = [];

    constructor(
        private readonly app: pc.AppBase,
        private readonly cameraComponent: pc.CameraComponent,
        private readonly worldLayer: pc.Layer,
        private readonly translucentLayer: pc.Layer,
        scale: number,
        readonly kind: SplatDepthPassKind = 'full'
    ) {
        super(app.graphicsDevice);
        const device = app.graphicsDevice;
        this.floatTarget = device.textureFloatRenderable;
        this.depthTexture = new pc.Texture(device, {
            name: kind === 'full' ? 'SplatLinearDepth' : 'SplatLinearDepthWater',
            width: 1,
            height: 1,
            format: this.floatTarget ? pc.PIXELFORMAT_R32F : pc.PIXELFORMAT_RGBA8,
            mipmaps: false,
            minFilter: pc.FILTER_NEAREST,
            magFilter: pc.FILTER_NEAREST,
            addressU: pc.ADDRESS_CLAMP_TO_EDGE,
            addressV: pc.ADDRESS_CLAMP_TO_EDGE,
        });
        const renderTarget = new pc.RenderTarget({
            name: kind === 'full' ? 'SplatDepthRT' : 'SplatDepthWaterRT',
            colorBuffer: this.depthTexture,
            depth: true,
            samples: 1,
        });
        this.init(renderTarget, { scaleX: scale, scaleY: scale });
        this.bindUniforms(false);
    }

    /** Point the translucent materials (and the water, at its own map) at the depth map; `valid` = false makes them skip the test. */
    bindUniforms(valid: boolean): void {
        if (this.kind === 'full') bindSplatDepthUniforms(this.device, this.depthTexture, valid, this.params);
        // the water's split map tells the water its empty value (the far clip it is cleared to):
        // empty there means only splats in front of the water, which the frame copy lacks too
        if (this.kind === 'water-split') bindSplatDepthUniforms(this.device, this.depthTexture, valid, this.params, WATER_UNIFORM_NAMES, this.cameraComponent.farClip);
        else if (this.servesWater) bindSplatDepthUniforms(this.device, this.depthTexture, valid, this.params, WATER_UNIFORM_NAMES);
    }

    frameUpdate(): void {
        super.frameUpdate();
        const farClip = this.cameraComponent.farClip;
        const clear = new pc.Color(farClip, 0, 0, 0);
        if (!this.floatTarget) {
            // Same packing the engine's prepass uses for RGBA8 targets.
            const bits = new Uint32Array(new Float32Array([farClip]).buffer)[0];
            clear.set(((bits >>> 24) & 0xff) / 255, ((bits >>> 16) & 0xff) / 255, ((bits >>> 8) & 0xff) / 255, (bits & 0xff) / 255);
        }
        this.setClearColor(clear);
        this.setClearDepth(1);
    }

    execute(): void {
        this.rendered = false;
        const camera = this.cameraComponent.camera;
        if (this.kind === 'full') {
            const translucent = (this.translucentLayer as LayerWithCulling).getCulledInstances(camera);
            // Fully transparent entities (entity opacity ~0) draw nothing, so they need no splat depth.
            const entitiesNeedIt = !!translucent && hasVisibleEntityTranslucency(translucent.transparent);
            if (!entitiesNeedIt && !this.alsoNeeded?.() && !(this.servesWater && this.waterNeeded?.())) return;
        }

        const culled = (this.worldLayer as LayerWithCulling).getCulledInstances(camera);
        const splats = this.splatInstances;
        const unified = this.unifiedInstances;
        splats.length = 0;
        unified.length = 0;
        if (culled) {
            // Only the splats: other World-layer draws (the editor's helpers, playground balls)
            // write the depth buffer themselves (splatMeshInstances.ts)
            const draws = collectUnifiedSplatDraws(this.app, this.unifiedSplatDraws);
            for (const list of [culled.transparent, culled.opaque]) {
                for (const mi of list) {
                    if (!isSplatMeshInstance(mi, draws)) continue;
                    // the water's split map: the unified draws go through the split's depth mode
                    if (this.kind === 'water-split' && draws.has(mi)) unified.push(mi);
                    else splats.push(mi);
                }
            }
        }
        const target = this.renderTarget!;
        const split = this.kind === 'water-split' ? this.waterSplit : null;
        const alphaClip = splatAlphaClip(this.app);
        // CPU-sort renderer: the prepass shader with the splats in front of the water culled in the
        // vertex stage (measured about half the cost of the hook's depth mode on WebGL2)
        const vertexCull = unified.length > 0 ? split?.vertexCullPlane?.() ?? null : null;
        if (vertexCull) {
            for (const mi of unified) splats.push(mi);
            unified.length = 0;
        } else if (unified.length > 0 && !split?.bindDepthMode(target, alphaClip)) {
            // the split can't draw this target: the full map (the water hides behind every splat, as without the split)
            for (const mi of unified) splats.push(mi);
            unified.length = 0;
        }
        const all = unified.length ? [...splats, ...unified] : splats;
        if (all.length > 0) {
            // The splat materials blend and do not write depth. For this pass only, flip the raw
            // blend/depth flags (not material.blendType, whose setter would move the draw between
            // sub-layers mid-frame) so the nearest splat above alphaClip wins the depth test.
            const saved = all.map((mi) => {
                const m = mi.material as unknown as BlendDepthState;
                const state = { blend: m.blendState.blend, write: m.depthState.write, test: m.depthState.test };
                m.blendState.blend = false;
                m.depthState.write = true;
                m.depthState.test = true;
                return state;
            });
            // The forward shader sizes each splat's quad down to its forward alpha clip (1/255) and
            // the prepass shader to alphaClip: draw the depth mode's quads as small as the prepass's
            // (several times fewer fragments, all of which it would discard anyway).
            const forwardClips = unified.map((mi) => {
                const material = mi.material as pc.Material;
                const value = (material.getParameter('alphaClipForward') as { data?: unknown } | undefined)?.data;
                material.setParameter('alphaClipForward', alphaClip);
                return value;
            });
            try {
                const renderer = (this.app as unknown as { renderer: ForwardRendererInternals }).renderer;
                // the cull plane goes in right before each draw: the main camera's projection
                // callback clears it when the layer's camera uniforms are set
                const clipScope = vertexCull ? this.device.scope.resolve(MIRROR_CLIP_PLANE_UNIFORM) : null;
                const drawCallback = clipScope ? () => clipScope.setValue(vertexCull) : undefined;
                try {
                    if (splats.length) renderer.renderForwardLayer(camera, target, null, undefined, pc.SHADER_PREPASS, { meshInstances: splats, drawCallback });
                } finally {
                    clipScope?.setValue(CLIP_PLANE_OFF);
                }
                // the forward shader pass runs the split's fragment hook, which writes the linear depth
                if (unified.length) renderer.renderForwardLayer(camera, target, null, undefined, pc.SHADER_FORWARD, { meshInstances: unified });
            } finally {
                all.forEach((mi, i) => {
                    const m = mi.material as unknown as BlendDepthState;
                    m.blendState.blend = saved[i].blend;
                    m.depthState.write = saved[i].write;
                    m.depthState.test = saved[i].test;
                });
                unified.forEach((mi, i) => {
                    const material = mi.material as pc.Material;
                    const value = forwardClips[i];
                    if (value === undefined) material.deleteParameter('alphaClipForward');
                    else material.setParameter('alphaClipForward', value as number);
                });
                splats.length = 0;
                unified.length = 0;
                split?.bindOff();
            }
        }
        this.rendered = true;
    }

    after(): void {
        this.bindUniforms(this.rendered);
    }

    destroy(): void {
        super.destroy();
        this.renderTarget?.destroy();
        this.depthTexture.destroy();
    }
}

/** The splat vertex clip plane every composed splat vertex chunk reads (effects/gsplat-mirror-clip.ts). */
const MIRROR_CLIP_PLANE_UNIFORM = 'uMirrorClipPlane';
const CLIP_PLANE_OFF = new Float32Array([0, 0, 0, 0]);
const SPLAT_UNIFORM_NAMES = { map: SPLAT_DEPTH_MAP_UNIFORM, params: SPLAT_DEPTH_PARAMS_UNIFORM };
const WATER_UNIFORM_NAMES = { map: WATER_DEPTH_MAP_UNIFORM, params: WATER_DEPTH_PARAMS_UNIFORM };

/** The alpha above which a splat enters the depth map (the engine's prepass clip, 0.3 by default). */
function splatAlphaClip(app: pc.AppBase): number {
    const clip = (app.scene as unknown as { gsplat?: { alphaClip?: number } }).gsplat?.alphaClip;
    return typeof clip === 'number' && Number.isFinite(clip) ? clip : 0.3;
}

/**
 * Write the splat-depth uniforms to the device scope (shared by the pass and the idle placeholder).
 * `emptyDepth` (params.w): set for the water's split map, the value its empty texels hold.
 */
function bindSplatDepthUniforms(device: pc.GraphicsDevice, texture: pc.Texture, valid: boolean, params: Float32Array, names = SPLAT_UNIFORM_NAMES, emptyDepth = 0): void {
    const scope = device.scope;
    scope.resolve(names.map).setValue(texture);
    params[0] = valid ? 1 : 0;
    params[1] = SPLAT_DEPTH_SOFT_FRACTION;
    params[2] = SPLAT_DEPTH_SOFT_MIN;
    params[3] = emptyDepth;
    scope.resolve(names.params).setValue(params);
}

/** Default idle time after which an unused pass and its full-size textures are freed. */
export const SPLAT_DEPTH_RELEASE_AFTER_MS = 2000;

/**
 * Tracks how long something has gone unneeded. `tick(false, now)` returns true once, when the
 * idle time first reaches `releaseAfterMs`; any `tick(true, …)` resets it.
 */
export class IdleReleaseTimer {
    private idleSince = -1;
    constructor(private readonly releaseAfterMs: number) {}
    tick(needed: boolean, now: number): boolean {
        if (needed) {
            this.idleSince = -1;
            return false;
        }
        if (this.idleSince < 0) {
            this.idleSince = now;
            return this.releaseAfterMs <= 0;
        }
        if (now - this.idleSince >= this.releaseAfterMs) {
            this.idleSince = Number.POSITIVE_INFINITY; // released; do not fire again until needed
            return true;
        }
        return false;
    }
    reset(): void {
        this.idleSince = -1;
    }
}

/**
 * Adds a SplatDepthPass in front of the main camera's first pass whenever `isNeeded()` is true,
 * by wrapping the renderer's per-frame frame-graph build. Returns an uninstall function.
 * When the pass is left out, the translucent materials are told the depth map is invalid, so
 * they render without the splat test instead of sampling a stale texture. After
 * `releaseAfterMs` without being needed the pass is destroyed (its full-size depth texture and
 * render target freed) and the materials point at a 1x1 placeholder until it is needed again.
 *
 * The water (`waterNeeded`) takes this pass's map under its own names, except while the water
 * split is on (`waterSplit`), when a second pass renders its map from the splats behind the water
 * only; the full pass then runs only for its other readers. The two have their own idle release.
 */
export function installSplatDepthPass(
    app: pc.AppBase,
    cameraComponent: pc.CameraComponent,
    worldLayer: pc.Layer,
    translucentLayer: pc.Layer,
    options: {
        scale: number;
        /** Translucent entities, particles, shadows (before culling). */
        isNeeded: () => boolean;
        /** The same after culling (in the pass); translucent entities are checked by the pass itself. */
        alsoNeeded?: () => boolean;
        /** Water planes on screen read the map (as uWaterDepthMap). */
        waterNeeded?: () => boolean;
        /** The water split, when the viewer has one (null while it isn't created yet). */
        waterSplit?: () => SplatDepthWaterSplit | null;
        releaseAfterMs?: number;
        now?: () => number;
    }
): { pass: () => SplatDepthPass | null; waterPass: () => SplatDepthPass | null; uninstall: () => void } {
    const renderer = (app as unknown as { renderer: ForwardRendererInternals }).renderer;
    const original = renderer.buildFrameGraph;
    const device = app.graphicsDevice;
    const now = options.now ?? (() => performance.now());
    const idle = new IdleReleaseTimer(options.releaseAfterMs ?? SPLAT_DEPTH_RELEASE_AFTER_MS);
    const waterIdle = new IdleReleaseTimer(options.releaseAfterMs ?? SPLAT_DEPTH_RELEASE_AFTER_MS);
    const placeholderParams = new Float32Array(4);
    const waterPlaceholderParams = new Float32Array(4);
    // Always-valid binding for the depth-map sampler while no pass exists (never sampled: the
    // params mark it invalid), so no translucent draw ever binds a missing or destroyed texture.
    const placeholder = new pc.Texture(device, {
        name: 'SplatLinearDepthPlaceholder',
        width: 1,
        height: 1,
        format: device.textureFloatRenderable ? pc.PIXELFORMAT_R32F : pc.PIXELFORMAT_RGBA8,
        mipmaps: false,
        minFilter: pc.FILTER_NEAREST,
        magFilter: pc.FILTER_NEAREST,
    });
    const bindPlaceholder = () => bindSplatDepthUniforms(device, placeholder, false, placeholderParams);
    const bindWaterPlaceholder = () => bindSplatDepthUniforms(device, placeholder, false, waterPlaceholderParams, WATER_UNIFORM_NAMES);
    bindPlaceholder();
    bindWaterPlaceholder();
    let pass: SplatDepthPass | null = null;
    let waterPass: SplatDepthPass | null = null;
    const releasePass = () => {
        if (!pass) return;
        pass.destroy();
        pass = null;
        bindPlaceholder();
    };
    const releaseWaterPass = () => {
        if (!waterPass) return;
        waterPass.destroy();
        waterPass = null;
    };

    renderer.buildFrameGraph = function (frameGraph: FrameGraphInternals, layerComposition: pc.LayerComposition) {
        original.call(this, frameGraph, layerComposition);
        const xrActive = !!(cameraComponent.camera as unknown as { xrActive?: boolean }).xrActive;
        const water = !!options.waterNeeded?.();
        const split = water ? options.waterSplit?.() ?? null : null;
        // the water's own map: only the splats behind it (the split draws the rest after the water)
        const splitOn = !!split?.active() && device.textureFloatRenderable;
        const othersNeed = options.isNeeded();
        const needed = othersNeed || (water && !splitOn);
        const waterSplitNeeded = water && splitOn;
        const rendering = !xrActive && cameraComponent.enabled;
        const insert: SplatDepthPass[] = [];
        if (!needed || !rendering) {
            if (pass) {
                pass.servesWater = false;
                pass.bindUniforms(false);
            } else bindPlaceholder();
            // Only the absence of translucent entities counts as idle; XR or a disabled camera
            // with translucent entities present keeps the pass for when rendering resumes.
            if (idle.tick(needed, now())) releasePass();
        } else {
            idle.reset();
            if (!pass) {
                pass = new SplatDepthPass(app, cameraComponent, worldLayer, translucentLayer, options.scale);
                pass.alsoNeeded = options.alsoNeeded ?? null;
                pass.waterNeeded = options.waterNeeded ?? null;
            }
            pass.servesWater = water && !splitOn;
            insert.push(pass);
        }
        if (!waterSplitNeeded || !rendering) {
            if (waterIdle.tick(waterSplitNeeded, now())) releaseWaterPass();
        } else {
            waterIdle.reset();
            if (!waterPass) waterPass = new SplatDepthPass(app, cameraComponent, worldLayer, translucentLayer, options.scale, 'water-split');
            waterPass.waterSplit = split;
            insert.push(waterPass);
        }
        // nothing renders the water's map this frame: it reads "no depth"
        if (!insert.some((p) => p.kind === 'water-split' || p.servesWater)) bindWaterPlaceholder();
        if (!insert.length) return;
        const passes = frameGraph.renderPasses;
        const cameraPasses = (cameraComponent.camera as unknown as { framePasses?: pc.RenderPass[] }).framePasses ?? [];
        let index = passes.findIndex((p) => {
            if (cameraPasses.includes(p)) return true;
            const steps = (p as unknown as { layerRenderSteps?: Array<{ cameraComponent?: pc.CameraComponent }> }).layerRenderSteps;
            return !!steps?.some((step) => step.cameraComponent === cameraComponent);
        });
        if (index < 0) index = passes.length;
        for (const p of insert) p.frameUpdate();
        passes.splice(index, 0, ...insert);
    };

    return {
        pass: () => pass,
        waterPass: () => waterPass,
        uninstall: () => {
            renderer.buildFrameGraph = original;
            pass?.destroy();
            pass = null;
            waterPass?.destroy();
            waterPass = null;
            placeholder.destroy();
        },
    };
}
